<template>
  <div class="task-list">
    <v-text-field
      label="Suche"
      variant="filled"
      v-model="fulltextSearch"
      append-inner-icon="mdi-magnify"
      @click:append-inner="doSearch"
      @keypress.enter="doSearch"
      hide-details
      :loading="store.loading"
    />
    <v-container v-if="!store.loading && store.displayFilters.length > 0" fluid>
      <filter-list :store="store" />
    </v-container>
    <div v-if="!store.loading && !store.error">
      <v-list>
        <template v-for="task in store.data" :key="task.AuftragNr">
          <v-list-item
            link
            :to="{
              name: 'customer_task_details',
              params: { id: $route.params.id, task_id: task.AuftragNr },
            }"
            class="order-item"
          >

              <v-list-item-title>Auftrag {{ task.AuftragNr }}</v-list-item-title>
              <v-list-item-subtitle>{{
                `${task.DatumFormatted} ${task.UhrZeit} Uhr`
              }}</v-list-item-subtitle>

            <template #append>
              <v-list-item-action>
                  <v-icon :color="task.color">{{ task.icon }}</v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
          <hr
            role="separator"
            aria-orientation="horizontal"
            class="v-divider"
            theme="light"
          />
        </template>
      </v-list>
      <v-container fluid class="mb-10">
        <pagination :module="store.$id" />
      </v-container>
    </div>
    <template v-else-if="store.error">
      <v-container class="py-1" fluid>
        <error :module="store.$id" />
      </v-container>
    </template>
    <template v-else>
      <loading />
    </template>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import FilterList from '@/components/FilterList.vue';
import Pagination from '@/components/Pagination.vue';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import {useStorage} from '@/composables/storage';
import useTasksStore from '@/store/tasks';
import {useFilterSort} from '@/composables/filterSort';

const emits = defineEmits(['close']);
const store = useTasksStore();
useStorage(
    store,
    (tasks, route) => route?.params.name ? `Aufträge ${route?.params.name}` : 'Aufträge',
    (route, _originalFilters, defaultFilters) => {
      return {
        ...defaultFilters,
        KundenNr: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id),
      };
    },
);
const {filters, save} = useFilterSort(store, emits);

const searchDebounceTimeout = ref<NodeJS.Timeout | null>(null);

const fulltextSearch = computed({
  get(): string {
    return filters.value.Fulltext$Fulltext.words.join(' ');
  },
  set(val: string) {
    if (!val) {
      filters.value.Fulltext$Fulltext.words = [];
      return;
    }
    filters.value.Fulltext$Fulltext.words = val.split(' ');
  },
});

watch(fulltextSearch, (newVal: string, oldVal: string) => {
  if (newVal === oldVal || newVal.length < 3) return;
  if (searchDebounceTimeout.value) clearTimeout(searchDebounceTimeout.value);

  searchDebounceTimeout.value = setTimeout(() => {
    doSearch();
  }, 500);
});

function doSearch() {
  if (searchDebounceTimeout.value) clearTimeout(searchDebounceTimeout.value);
  save();
}

defineExpose({
  store,
  filters,
  fulltextSearch,
  searchDebounceTimeout,

  FilterList,
  Pagination,
  Error,
  Loading,
});

</script>
